import { isEqual } from "lodash";

export type CustomFieldColumn = {
  field: string;
  field_type?: string;
  headerName?: string;
};

/** Mutation / compare key: strips `_custom_fields.` and trailing `_obj`. */
export function getCustomFieldApplyKey(colField: string): string {
  return colField.replace(/^_custom_fields\./, "").replace(/_obj$/, "");
}

/** Key CustomFields uses to read display state from `_custom_fields`. */
export function getCustomFieldDisplayKey(colField: string): string {
  return colField.replace(/^_custom_fields\./, "");
}

function getRowCustomFields(row: any): Record<string, any> {
  return row?._custom_fields || {};
}

/**
 * Comparable scalar/id used to decide whether selected rows share a value.
 */
export function getComparableCustomFieldValue(
  row: any,
  col: CustomFieldColumn
): any {
  const source = getRowCustomFields(row);
  const applyKey = getCustomFieldApplyKey(col.field);

  if (col.field_type === "user" || col.field_type === "vendor") {
    const obj = source[`${applyKey}_obj`] ?? source[applyKey];
    if (obj && typeof obj === "object") return obj.id ?? null;
    return obj ?? null;
  }

  return source[applyKey] ?? null;
}

/**
 * Display shape expected by `CustomFields` for a single column from a row.
 */
export function getDisplayCustomFieldEntries(
  row: any,
  col: CustomFieldColumn
): Record<string, any> {
  const source = getRowCustomFields(row);
  const applyKey = getCustomFieldApplyKey(col.field);
  const displayKey = getCustomFieldDisplayKey(col.field);
  const entries: Record<string, any> = {};

  if (col.field_type === "user" || col.field_type === "vendor") {
    const obj =
      source[`${applyKey}_obj`] ??
      (source[applyKey] && typeof source[applyKey] === "object"
        ? source[applyKey]
        : source[applyKey] != null
          ? { id: source[applyKey] }
          : null);
    entries[displayKey] = obj;
    return entries;
  }

  if (col.field_type === "geolocation") {
    entries[applyKey] = source[applyKey] ?? null;
    if (source[`${applyKey}_obj`]) {
      entries[`${applyKey}_obj`] = source[`${applyKey}_obj`];
    }
    return entries;
  }

  entries[displayKey] = source[applyKey] ?? source[displayKey] ?? null;
  return entries;
}

/**
 * Prefill custom fields when every selected row shares the same value.
 * Mixed values are omitted (blank in the UI), matching regular bulk-edit fields.
 */
export function getConsensusCustomFields(
  rows: any[],
  columns: CustomFieldColumn[] | undefined | null
): {
  initialApplyValues: Record<string, any>;
  initialDisplayValues: Record<string, any>;
} {
  const initialApplyValues: Record<string, any> = {};
  const initialDisplayValues: Record<string, any> = {};

  if (!rows?.length || !columns?.length) {
    return { initialApplyValues, initialDisplayValues };
  }

  columns.forEach((col) => {
    const applyKey = getCustomFieldApplyKey(col.field);
    const values = rows.map((row) => getComparableCustomFieldValue(row, col));
    const allSame = values.every((value) => isEqual(value, values[0]));

    if (!allSame) return;

    initialApplyValues[applyKey] = values[0] ?? null;
    Object.assign(
      initialDisplayValues,
      getDisplayCustomFieldEntries(rows[0], col)
    );
  });

  return { initialApplyValues, initialDisplayValues };
}

/**
 * Overlay user edits onto prefilled display values for `CustomFields`.
 */
export function mergeCustomFieldDisplayValues(
  initialDisplayValues: Record<string, any>,
  customFieldChanges: Record<string, any>,
  columns: CustomFieldColumn[] | undefined | null
): Record<string, any> {
  const merged = { ...initialDisplayValues };

  Object.entries(customFieldChanges).forEach(([applyKey, value]) => {
    const col = columns?.find(
      (c) => getCustomFieldApplyKey(c.field) === applyKey
    );
    const displayKey = col ? getCustomFieldDisplayKey(col.field) : applyKey;

    if (col?.field_type === "user" || col?.field_type === "vendor") {
      merged[displayKey] = value ? { id: value } : null;
      return;
    }

    if (col?.field_type === "geolocation") {
      merged[applyKey] = value;
      if (value == null) {
        delete merged[`${applyKey}_obj`];
      }
      return;
    }

    merged[displayKey] = value;
  });

  return merged;
}

export function getChangedCustomFieldsPayload(
  customFieldChanges: Record<string, any>,
  initialApplyValues: Record<string, any>
): Record<string, any> {
  const payload: Record<string, any> = {};

  Object.entries(customFieldChanges).forEach(([key, value]) => {
    const initial = Object.prototype.hasOwnProperty.call(
      initialApplyValues,
      key
    )
      ? initialApplyValues[key]
      : null;
    if (!isEqual(value, initial)) {
      payload[key] = value;
    }
  });

  return payload;
}

export function hasCustomFieldDiff(
  customFieldChanges: Record<string, any>,
  initialApplyValues: Record<string, any>
): boolean {
  return (
    Object.keys(
      getChangedCustomFieldsPayload(customFieldChanges, initialApplyValues)
    ).length > 0
  );
}
